import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Barcode from 'react-barcode';
import {
  Card,
  Badge,
  Button,
  Select,
  Row,
  Col,
} from '@folio/stripes/components';
import RealBarcodeGenerator from './RealBarcodeGenerator'; // kware: إضافة مولد الباركود الحقيقي


const ProfessionalBarcodePreview = ({ items, settings }) => {
  const intl = useIntl();
  const canvasRef = useRef(null);
  const barcodeGenerator = useRef(new RealBarcodeGenerator()); // kware: إضافة مولد الباركود الحقيقي
  const [previewMode, setPreviewMode] = useState('actual'); // actual, scaled, print

  /**
   * Convert settings to actual measurements
   */
  const getActualMeasurements = () => {
    const { printerType, labelWidth = 4, labelHeight = 2 } = settings;

    // Standard measurements in inches
    const measurements = {
      ZEBRA_ZPL: {
        labelWidth: 4.0,   // 4 inches
        labelHeight: 2.0,  // 2 inches
        dpi: 203,          // Standard Zebra DPI
        barcodeHeight: 0.5, // 0.5 inches
        fontSize: 8,       // 8pt font
        margin: 0.125      // 1/8 inch margin
      },
      ESC_POS: {
        labelWidth: 2.25,  // Receipt width
        labelHeight: 1.5,
        dpi: 180,
        barcodeHeight: 0.4,
        fontSize: 7,
        margin: 0.1
      },
      BROTHER_PTOUCH: {
        labelWidth: 1.0,   // P-Touch tape width
        labelHeight: 0.5,
        dpi: 180,
        barcodeHeight: 0.3,
        fontSize: 6,
        margin: 0.05
      }
    };

    return measurements[printerType] || measurements.ZEBRA_ZPL;
  };

  /**
   * Generate professional barcode preview canvas
   */
  const generateCanvasPreview = () => {
    const canvas = canvasRef.current;
    if (!canvas || items.length === 0) return;

    const ctx = canvas.getContext('2d');
    const measurements = getActualMeasurements();

    // Scale factor for display (96 DPI for screen)
    const scale = previewMode === 'actual' ? 96 / measurements.dpi : 1;
    const pixelsPerInch = measurements.dpi * scale;

    // Calculate canvas dimensions
    const labelWidthPx = measurements.labelWidth * pixelsPerInch;
    const labelHeightPx = measurements.labelHeight * pixelsPerInch;
    const itemsPerRow = Math.floor(600 / labelWidthPx) || 1;
    const totalRows = Math.ceil(items.length / itemsPerRow);

    canvas.width = labelWidthPx * itemsPerRow;
    canvas.height = labelHeightPx * totalRows;

    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw each label
    items.forEach((item, index) => {
      const row = Math.floor(index / itemsPerRow);
      const col = index % itemsPerRow;
      const x = col * labelWidthPx;
      const y = row * labelHeightPx;

      drawLabel(ctx, item, x, y, labelWidthPx, labelHeightPx, measurements, scale);
    });
  };

  /**
   * Draw individual label on canvas
   */
  const drawLabel = (ctx, item, x, y, width, height, measurements, scale) => {
    const margin = measurements.margin * 96 * scale;
    const barcodeHeight = measurements.barcodeHeight * 96 * scale;
    const fontSize = measurements.fontSize * scale;

    // Draw label border
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, width, height);

    // Draw real barcode (kware: استبدال المحاكاة بباركود حقيقي)
    const barcodeX = x + margin;
    const barcodeY = y + margin;
    const barcodeWidth = width - (margin * 2);

    // Generate real barcode and draw it
    try {
      const barcodeCanvas = barcodeGenerator.current.generateBarcodeElement(
        item.barcode,
        settings.format || 'CODE128',
        {
          width: 2,
          height: barcodeHeight / scale,
          displayValue: false,
          background: '#ffffff',
          lineColor: '#000000',
          margin: 0
        }
      );

      if (barcodeCanvas) {
        ctx.drawImage(barcodeCanvas, barcodeX, barcodeY, barcodeWidth, barcodeHeight);
      }
    } catch (error) {
      // Fallback to error display
      ctx.fillStyle = '#ff0000';
      ctx.fillRect(barcodeX, barcodeY, barcodeWidth, barcodeHeight);
      ctx.fillStyle = '#ffffff';
      ctx.font = `${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('Error', barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight / 2);
    }

    // Draw text if enabled
    if (settings.includeText) {
      ctx.fillStyle = '#000000';
      ctx.font = `${fontSize}px Arial`;
      ctx.textAlign = 'center';

      // Barcode text
      const textY = barcodeY + barcodeHeight + (fontSize * 1.2);
      ctx.fillText(item.barcode, x + width / 2, textY);

      // Item info
      ctx.font = `${fontSize * 0.8}px Arial`;
      const infoY = textY + (fontSize * 1.5);
      ctx.fillText(item.materialType?.name || 'N/A', x + width / 2, infoY);
      ctx.fillText(item.effectiveLocation?.name || 'N/A', x + width / 2, infoY + (fontSize * 1.2));
    }
  };

  /**
   * Generate ZPL preview text
   */
  const generateZPLPreview = (item) => {
    const measurements = getActualMeasurements();
    const dpi = measurements.dpi;
    const labelWidthDots = measurements.labelWidth * dpi;
    const labelHeightDots = measurements.labelHeight * dpi;
    const barcodeHeightDots = measurements.barcodeHeight * dpi;

    return `^XA
^LL${labelHeightDots}
^PW${labelWidthDots}
^FO50,50^BY3,2,${barcodeHeightDots}^BCN,,Y,N^FD${item.barcode}^FS
${settings.includeText ? `^FO50,${50 + barcodeHeightDots + 20}^A0N,25,25^FD${item.barcode}^FS` : ''}
${settings.includeText ? `^FO50,${50 + barcodeHeightDots + 60}^A0N,20,20^FD${item.materialType?.name || 'N/A'}^FS` : ''}
${settings.includeText ? `^FO50,${50 + barcodeHeightDots + 90}^A0N,20,20^FD${item.effectiveLocation?.name || 'N/A'}^FS` : ''}
^XZ`;
  };

  // Update canvas when settings or items change
  useEffect(() => {
    generateCanvasPreview();
  }, [items, settings, previewMode]);

  const previewModeOptions = [
    {
      value: 'actual',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.preview.mode.actual',
        defaultMessage: 'Actual Size'
      })
    },
    {
      value: 'scaled',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.preview.mode.scaled',
        defaultMessage: 'Scaled to Fit'
      })
    }
  ];

  const measurements = getActualMeasurements();

  return (
    <Card>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h4 style={{ margin: 0, marginRight: '8px' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.preview.professional.title"
                defaultMessage="Professional Preview"
              />
            </h4>
            <Badge color="primary">{items.length}</Badge>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Select
              dataOptions={previewModeOptions}
              value={previewMode}
              onChange={(e) => setPreviewMode(e.target.value)}
              marginBottom0
            />
          </div>
        </div>

        {/* Technical Info */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px',
          fontSize: '12px'
        }}
        >
          <Row>
            <Col xs={3}>
              <strong>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodePrinting.preview.printer"
                  defaultMessage="Printer:"
                />
              </strong>
              <div>{settings.printerType || 'ZEBRA_ZPL'}</div>
            </Col>
            <Col xs={3}>
              <strong>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodePrinting.preview.labelSize"
                  defaultMessage="Label Size:"
                />
              </strong>
              <div>{measurements.labelWidth}" × {measurements.labelHeight}"</div>
            </Col>
            <Col xs={3}>
              <strong>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodePrinting.preview.resolution"
                  defaultMessage="Resolution:"
                />
              </strong>
              <div>{measurements.dpi} DPI</div>
            </Col>
            <Col xs={3}>
              <strong>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodePrinting.preview.copies"
                  defaultMessage="Total Labels:"
                />
              </strong>
              <div>{items.length * settings.copies}</div>
            </Col>
          </Row>
        </div>

        {/* Canvas Preview */}
        <div style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '16px',
          backgroundColor: '#fff',
          textAlign: 'center',
          maxHeight: '500px',
          overflowY: 'auto'
        }}
        >
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              border: '1px solid #eee'
            }}
          />
        </div>

        {/* ZPL Code Preview for first item */}
        {settings.printerType === 'ZEBRA_ZPL' && items.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <strong>
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.preview.zplCode"
                defaultMessage="ZPL Code Sample:"
              />
            </strong>
            <pre style={{
              backgroundColor: '#f8f9fa',
              padding: '12px',
              borderRadius: '4px',
              fontSize: '11px',
              maxHeight: '200px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap'
            }}
            >
              {generateZPLPreview(items[0])}
            </pre>
          </div>
        )}

        {items.length > 3 && (
          <div style={{
            textAlign: 'center',
            marginTop: '12px',
            fontSize: '12px',
            color: '#666'
          }}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.barcodePrinting.preview.showing"
              defaultMessage="Showing preview for {count} items"
              values={{ count: Math.min(items.length, 10) }}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

ProfessionalBarcodePreview.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  settings: PropTypes.shape({
    format: PropTypes.string.isRequired,
    includeText: PropTypes.bool.isRequired,
    copies: PropTypes.number.isRequired,
    printerType: PropTypes.string,
    labelWidth: PropTypes.number,
    labelHeight: PropTypes.number,
  }).isRequired,
};

export default ProfessionalBarcodePreview;
